import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { Login } from "../components/AuthenticationWizard/Login";
import { Registration } from "../components/AuthenticationWizard/Registration";
import { SuccessStep } from "../components/AuthenticationWizard/SuccessStep";
import { ChangePassword } from "../components/ChangePassword";
import { ProfileEditForm } from "../components/ProfileEditForm";
import { eventBus } from "../eventBus";
import { useAuth } from "../hooks/useAuth";
import { useRouter } from "../hooks/useRouter";
import { paths } from "../types/Defaults";
import { useCookies } from "react-cookie";
import SubscribeRequest from "../components/AuthenticationWizard/SubscribeRequest";

type HeaderProps = {
  children?: ReactNode;
  className?: string;
  childrenAlignementLeft?: boolean;
  viewType: "longOnly" | "longShort" | "portfolios";
};

type RegistrationData = {
  email: string;
  fullName: string;
};

type LoginData = {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  firm: string;
  country: string;
  role: string;
};

type HeaderUserBoxProps = {
  setShowUserBox: Function;
  initialTab?: WizardStateType;
};

type EditProfileInfo = {
  id: number;
  email: string;
  profile: string;
  firstName: string;
  lastName: string;
  country: string;
  firm: string;
};

type WizardStateType = "Login" | "Registration" | "Success" | "Subscribe";

const HeaderUserBox = ({ setShowUserBox, initialTab }: HeaderUserBoxProps) => {
  const { isReady, isLogged, register, signin, signout, user, editProfile } =
    useAuth();
  const router = useRouter();
  const [isLogging, setIsLogging] = useState(false);
  const [loginFailedMessage, setLoginFailedMessage] = useState<string>("");
  const [hasLoginFailed, setLoginFailed] = useState<boolean>(false);
  const [showRegisterMessage, setShowRegisterMessage] =
    useState<boolean>(false);
  const [wizardState, setWizardState] = useState<WizardStateType>(
    initialTab ?? "Login",
  );

  const wizardSteps = useMemo(
    () => ({
      login: "Login",
      registration: "Registration",
      success: "Success",
      subscribe: "Subscribe",
      goToLogin: () => setWizardState("Login"),
      goToRegistration: () => setWizardState("Registration"),
      goToSuccess: () => setWizardState("Success"),
      goToSubscribe: () => setWizardState("Subscribe"),
    }),
    [],
  );

  const handleRegister = ({ email, fullName }: RegistrationData) => {
    setShowRegisterMessage(true);
    const registrationData = {
      email,
      firstName: "",
      lastName: "",
      userName: email,
    };

    const explodedName = fullName.trim().split(" ");

    if (explodedName.length === 0) {
      return;
    }

    if (explodedName.length > 2) {
      registrationData.firstName = explodedName[0];
      // In case of multiple white space we assume that the first is the name and the second is the last name so we rebuild the string
      registrationData.lastName = explodedName.slice(1).join(" ");
    } else {
      registrationData.firstName = explodedName[0];
      registrationData.lastName = explodedName?.[1] ?? "";
    }

    register(registrationData).then(() =>
      setTimeout(() => {
        setShowRegisterMessage(false);
      }, 1000),
    );
  };

  const handleLogout = () => {
    if (isReady) {
      signout().then(() => {
        setShowUserBox(false);
      });
    }
  };

  const handlePasswordChange = ({
    id,
    password,
  }: {
    id: number;
    password: string;
  }) => {
    if (isReady) {
      return editProfile({
        id,
        password,
      });
    }
  };

  const handleEditProfile = ({
    id,
    email,
    profile,
    firstName,
    lastName,
    country,
    firm,
  }: EditProfileInfo) => {
    if (isReady) {
      return editProfile({
        id,
        email,
        profile,
        firstName,
        lastName,
        country,
        firm,
      });
    }
  };

  const handleLogin = ({ email, password }: LoginData) => {
    if (isReady) {
      setIsLogging(true);
      signin(email, password).then(
        (response: any) => {
          setIsLogging(false);
          setLoginFailed(false);
          if (router.pathname === "/") {
            // If home, redirect to product page
            setTimeout(() => router.push("/product"), 300);
          }
          setShowUserBox(false);
        },
        (error: any) => {
          setIsLogging(false);
          setLoginFailed(true);
          if (error.type === "USER_NOT_FOUND") {
            setLoginFailedMessage("Your email and/or password is not correct.");
          } else {
            if (error.message != null) {
              setLoginFailedMessage(error.message);
            } else {
              // TODO what is that?
              setLoginFailedMessage(
                "An unknown error occurred. Please contact Systematic Gains.",
              );
            }
          }
        },
      );
    }
  };

  const AuthWizardComponent = () => {
    switch (wizardState) {
      case wizardSteps.registration:
        return (
          <Registration
            showRegisterMessage={showRegisterMessage}
            handleRegister={handleRegister}
            hasLoginFailed={hasLoginFailed}
            loginFailedMessage={loginFailedMessage}
            navigateToLogin={wizardSteps.goToLogin}
            navigateToSuccess={wizardSteps.goToSuccess}
          />
        );

      case wizardSteps.success:
        return <SuccessStep />;

      case wizardSteps.subscribe:
        return <SubscribeRequest navigateToSuccess={wizardSteps.goToSuccess} />;

      case wizardSteps.login:
      default:
        return (
          <Login
            handleLogin={handleLogin}
            hasLoginFailed={hasLoginFailed}
            loginFailedMessage={loginFailedMessage}
            showRegisterMessage={showRegisterMessage}
            navigateToRegistration={wizardSteps.goToRegistration}
          />
        );
    }
  };

  return (
    <div className="header-user-box">
      <Row>
        <Col sm={12}>
          <div className="d-flex justify-content-end align-items-center">
            <Button
              className="text-light text-decoration-none"
              onClick={() => {
                setShowUserBox(false);
                setWizardState("Login");
              }}
              variant="link"
            >
              <i className="bi bi-x-lg smaller-icon"></i>
              &nbsp;&nbsp;Close
            </Button>
          </div>
        </Col>
      </Row>
      <Container>
        <Row>
          <Col sm={12}>
            <div className="d-flex justify-content-center align-items-center pt-4 pt-sm-0 flex-column">
              {isLogged && wizardState !== wizardSteps.subscribe ? (
                <div className="text-center">
                  <ProfileEditForm
                    userData={user}
                    editHandler={handleEditProfile}
                  />
                  <ChangePassword
                    userData={user}
                    changeUserPassword={handlePasswordChange}
                  />
                  <div className="mt-5">
                    <Button
                      className="btn btn-outline-light"
                      onClick={handleLogout}
                    >
                      Logout {user.firstName} {user.lastName}
                    </Button>
                  </div>
                </div>
              ) : isLogging ? (
                <div className="fs-4 text-light">Logging user...</div>
              ) : (
                <AuthWizardComponent />
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export const Header = ({
  children,
  childrenAlignementLeft = false,
}: HeaderProps) => {
  const { isReady, isLogged, user } = useAuth();
  const { pathname, query } = useRouter();
  const [showUserBox, setShowUserBox] = useState<boolean>(false);

  const [predefinedTab, setPredefinedTab] = useState<
    WizardStateType | undefined
  >();

  const busShowUserBox: any = (showBox: boolean) => {
    setShowUserBox((prevShowBox) => {
      if (prevShowBox === showBox) {
        // If the box is already open, still move the page to the top
        window.scrollTo(0, 0);
      }
      return showBox;
    });
  };

  useEffect(() => {
    eventBus.on("show-user-login-box", busShowUserBox);
    return () => {
      eventBus.remove("show-user-login-box", busShowUserBox);
    };
  }, []);

  useEffect(() => {
    eventBus.on("show-user-registration-box", () => {
      setPredefinedTab("Registration");
      busShowUserBox(true);
    });
    return () => {
      eventBus.remove("show-user-registration-box", () => {
        setPredefinedTab("Registration");
        busShowUserBox(true);
      });
    };
  }, []);

  useEffect(() => {
    eventBus.on("show-user-subscribe-box", () => {
      setPredefinedTab("Subscribe");
      busShowUserBox(true);
    });
    return () => {
      eventBus.remove("show-user-subscribe-box", () => {
        setPredefinedTab("Subscribe");
        busShowUserBox(true);
      });
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (showUserBox) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
  }, [showUserBox]);

  useEffect(() => {
    if (isReady && !isLogged && query.has("login")) {
      setPredefinedTab("Login");
      setShowUserBox(true);
    }
  }, [isReady, isLogged, query]);
  const handleOpenLogin = useCallback(() => {
    setShowUserBox((showUserBox) => {
      setPredefinedTab("Login");

      return !showUserBox;
    });
  }, []);

  // const handleOpenSubscribe = useCallback(() => {
  //   setShowUserBox((showUserBox) => {
  //     setPredefinedTab("Subscribe");
  //     return !showUserBox;
  //   });
  // }, []);

  const showSubscribeBtn = useMemo(() => {
    return !isLogged;
    // || user?.type === TRIAL_USER_TYPE;
  }, [isLogged]);

  if (!isReady) {
    return (
      <header className="site-header">
        <Container fluid className="h-100">
          <Row className="h-100 d-flex align-items-center align-content-end align-content-md-center">
            <Col className="site-header__slot col-auto">
              <a href={paths.externalHome} className="text-decoration-none">
                <img
                  src="/images/logo.png"
                  alt="Systematic Gains"
                  className="site-header__logo"
                />
              </a>
            </Col>
          </Row>
        </Container>
      </header>
    );
  }

  return (
    <header
      className="site-header"
      style={{
        position: "sticky",
        top: 0,
        width: "100%",
        zIndex: 1000,
      }}
    >
      <Container fluid className="h-100">
        <Row className="h-100 d-flex align-items-center align-content-end align-content-md-center">
          <Col className="site-header__slot col-auto">
            <a href={paths.externalHome} className="text-decoration-none">
              <img
                src="/images/logo.png"
                alt="Systematic Gains"
                className="site-header__logo"
              />
            </a>
          </Col>

          {children ? (
            <Col
              className={`site-header__title col-12 col-md-auto ${childrenAlignementLeft ? "" : "ms-auto"} order-3 order-md-2`}
            >
              {children}
            </Col>
          ) : null}
          <Col
            className={`site-header__slot col-auto ${
              children && !childrenAlignementLeft ? "" : "ms-auto"
            } order-2 order-md-3`}
          >
            {pathname === paths.product && (
              <a
                href={paths.externalHome}
                className="rounded-3 btn btn-outline-light ms-2 d-none d-md-inline-block"
              >
                Back to Home
              </a>
            )}
            {showSubscribeBtn && (
              <Button
                // onClick={handleOpenSubscribe}
                variant={
                  showUserBox && predefinedTab === "Subscribe" ? "primary" : ""
                }
                href={paths.externalHome + "#subscribe"}
              >
                <span
                  style={{
                    backgroundColor: "#ffd700",
                    color: "black",
                  }}
                  className="site-header__subscribe-pill rounded"
                >
                  Subscribe
                </span>
              </Button>
            )}
            <Button
              className={`header-user text-end text-truncate shadow-none text-decoration-none text-light d-none d-md-inline-block ${
                showUserBox ? "active" : ""
              } ${isLogged ? "mx-3" : ""}`}
              onClick={handleOpenLogin}
              variant={
                showUserBox && predefinedTab === "Login" ? "primary" : "link"
              }
            >
              <i className="bi bi-person-circle"></i>{" "}
              {isLogged ? (
                `${user.firstName} ${user.lastName}`
              ) : (
                <LoginHeading />
              )}
            </Button>
            {showUserBox ? (
              <HeaderUserBox
                initialTab={predefinedTab}
                setShowUserBox={setShowUserBox}
              />
            ) : (
              ""
            )}
          </Col>
        </Row>
      </Container>
    </header>
  );
};

const LoginHeading = () => {
  const [cookie] = useCookies(["signUpCTA"]);
  const visible = cookie?.["signUpCTA"] === "false" ? false : true;

  if (!visible) {
    return (
      <span>
        Log In
        {/* /{" "}
        <span
          style={{
            backgroundColor: "#ffd700",
            color: "black",
            padding: "8px 16px",
            borderRadius: "8px",
          }}
        >
          Sign Up
        </span> */}
      </span>
    );
  }

  return <span>Log In</span>;
};
